import Vue from 'vue';


const samlNsTags = {
  entity: 'md:EntityDescriptor',
  ssoDescriptor: 'md:IDPSSODescriptor',
  keyDescriptor: 'md:KeyDescriptor',
  keyInfo: 'ds:KeyInfo',
  x509Data: 'ds:X509Data',
  x509Cert: 'ds:X509Certificate',
  slo: 'md:SingleLogoutService',
  sso: 'md:SingleSignOnService',
};
const ssoBinding = 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect';
const sloBinding = 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect';
const samlOasisNameUriFormat = 'urn:oasis:names:tc:SAML:2.0:attrname-format:uri';

/**
 * Util for samlInputFile: Fetch the first element of an element collection by tag name
 * @param  {Element} parent - Parent element from which search for the specified tag
 * @param  {String} nsTag - Namespaced tag to search in the parent's children
 * @param  {Object}  [conditionalAttribute] - Add an option to select the tag
 * @return {Element} Found element or null
 */
function getElementByTags(parent, nsTag, conditionalAttribute = null) {
  const els = getElementsByTags(parent, nsTag, conditionalAttribute);
  if (els.length === 0) {
    return null;
  }
  return els[0];
}

/**
 * Util for samlInputFile: Fetch the an elements collection by tag name, stripping the namespace if necesary
 * @param  {Element} parent - Parent element from which search for the specified tag
 * @param  {String} nsTag - Namespaced tag to search in the parent's children
 * @param  {Object}  [conditionalAttribute] - Add an option to select the tag
 * @return {Array} Found array of elements or empty
 */
function getElementsByTags(parent, nsTag, conditionalAttribute = null) {
  if (!parent.getElementsByTagName) {
    return [];
  }
  const usingNs = parent.getElementsByTagName(nsTag);
  let filtered = [];
  const elts = usingNs.length ? usingNs : parent.getElementsByTagName(nsTag.split(':')[1]);
  if (elts.length > 1 && conditionalAttribute !== null) {
    filtered = Array.from(elts).filter((elt) => {
      const keys = Object.keys(conditionalAttribute);
      for (let i = 0, len = keys.length; i < len; i++) {
        const attr = elt.getAttribute(keys[i]);
        if (attr !== conditionalAttribute[keys[i]]) {
          return false;
        }
      }
      return true;
    });
  }
  return filtered.length > 0 ? filtered : elts;
}

/**
 * Parse an IDP metadata XML file
 * @param  {String} xmlStr - Content of the XML file
 * @return {Object} Containing { entityId, ssoUrl, sloUrl, x509CertString }
 */
export function parseSamlIDPXml(xmlStr) {
  const dom = (new DOMParser()).parseFromString(xmlStr, 'application/xml');

  const entities = getElementsByTags(dom, samlNsTags.entity);

  let entity;
  if (entities.length > 1) {
    entity = [...entities].find(e => getElementsByTags(e, samlNsTags.ssoDescriptor).length > 0);
  } else if (entities.length === 1) {
    [entity] = entities;
  }
  if (!entity) {
    return false;
  }
  const entityId = entity.getAttribute('entityID');
  const ssoDesc = getElementByTags(entity, samlNsTags.ssoDescriptor);
  const keyDesc = getElementByTags(ssoDesc, samlNsTags.keyDescriptor, { 'use': 'signing' });
  const x509Cert = getElementByTags(keyDesc, samlNsTags.x509Cert);
  if (!x509Cert) {
    return false;
  }
  const x509CertString = x509Cert.textContent;

  const ssoServices = getElementsByTags(ssoDesc, samlNsTags.sso);
  if (ssoServices.length === 0) {
    return false;
  }
  const ssoService = [...ssoServices].find(s => s.getAttribute('Binding') === ssoBinding)
    || [...ssoServices].find(s => s.getAttribute('Location') && s.getAttribute('Location').length);
  const ssoUrl = ssoService.getAttribute('Location');

  const sloServices = getElementsByTags(ssoDesc, samlNsTags.slo);
  let sloUrl = '';
  if (sloServices.length > 0) {
    const sloService = [...sloServices].find(s => s.getAttribute('Binding') === sloBinding)
      || [...sloServices].find(s => s.getAttribute('Location') && s.getAttribute('Location').length);
    sloUrl = sloService.getAttribute('Location');
  }

  return { entityId, ssoUrl, sloUrl, x509CertString };
}

export const samlAttributeNames = {
  aRecord: { uri: 'urn:oid:0.9.2342.19200300.100.1.26', friendlyName: 'aRecord' },
  aliasedEntryName: { uri: 'urn:oid:2.5.4.1', friendlyName: 'aliasedEntryName' },
  aliasedObjectName: { uri: 'urn:oid:2.5.4.1', friendlyName: 'aliasedObjectName' },
  associatedDomain: { uri: 'urn:oid:0.9.2342.19200300.100.1.37', friendlyName: 'associatedDomain' },
  associatedName: { uri: 'urn:oid:0.9.2342.19200300.100.1.38', friendlyName: 'associatedName' },
  audio: { uri: 'urn:oid:0.9.2342.19200300.100.1.55', friendlyName: 'audio' },
  authorityRevocationList: { uri: 'urn:oid:2.5.4.38', friendlyName: 'authorityRevocationList' },
  buildingName: { uri: 'urn:oid:0.9.2342.19200300.100.1.48', friendlyName: 'buildingName' },
  businessCategory: { uri: 'urn:oid:2.5.4.15', friendlyName: 'businessCategory' },
  c: { uri: 'urn:oid:2.5.4.6', friendlyName: 'c' },
  cACertificate: { uri: 'urn:oid:2.5.4.37', friendlyName: 'cACertificate' },
  cNAMERecord: { uri: 'urn:oid:0.9.2342.19200300.100.1.31', friendlyName: 'cNAMERecord' },
  carLicense: { uri: 'urn:oid:2.16.840.1.113730.3.1.1', friendlyName: 'carLicense' },
  certificateRevocationList: { uri: 'urn:oid:2.5.4.39', friendlyName: 'certificateRevocationList' },
  cn: { uri: 'urn:oid:2.5.4.3', friendlyName: 'cn' },
  co: { uri: 'urn:oid:0.9.2342.19200300.100.1.43', friendlyName: 'co' },
  commonName: { uri: 'urn:oid:2.5.4.3', friendlyName: 'commonName' },
  countryName: { uri: 'urn:oid:2.5.4.6', friendlyName: 'countryName' },
  crossCertificatePair: { uri: 'urn:oid:2.5.4.40', friendlyName: 'crossCertificatePair' },
  dITRedirect: { uri: 'urn:oid:0.9.2342.19200300.100.1.54', friendlyName: 'dITRedirect' },
  dSAQuality: { uri: 'urn:oid:0.9.2342.19200300.100.1.49', friendlyName: 'dSAQuality' },
  dc: { uri: 'urn:oid:0.9.2342.19200300.100.1.25', friendlyName: 'dc' },
  deltaRevocationList: { uri: 'urn:oid:2.5.4.53', friendlyName: 'deltaRevocationList' },
  departmentNumber: { uri: 'urn:oid:2.16.840.1.113730.3.1.2', friendlyName: 'departmentNumber' },
  description: { uri: 'urn:oid:2.5.4.13', friendlyName: 'description' },
  destinationIndicator: { uri: 'urn:oid:2.5.4.27', friendlyName: 'destinationIndicator' },
  displayName: { uri: 'urn:oid:2.16.840.1.113730.3.1.241', friendlyName: 'displayName' },
  distinguishedName: { uri: 'urn:oid:2.5.4.49', friendlyName: 'distinguishedName' },
  dmdName: { uri: 'urn:oid:2.5.4.54', friendlyName: 'dmdName' },
  dnQualifier: { uri: 'urn:oid:2.5.4.46', friendlyName: 'dnQualifier' },
  documentAuthor: { uri: 'urn:oid:0.9.2342.19200300.100.1.14', friendlyName: 'documentAuthor' },
  documentIdentifier: { uri: 'urn:oid:0.9.2342.19200300.100.1.11', friendlyName: 'documentIdentifier' },
  documentLocation: { uri: 'urn:oid:0.9.2342.19200300.100.1.15', friendlyName: 'documentLocation' },
  documentPublisher: { uri: 'urn:oid:0.9.2342.19200300.100.1.56', friendlyName: 'documentPublisher' },
  documentTitle: { uri: 'urn:oid:0.9.2342.19200300.100.1.12', friendlyName: 'documentTitle' },
  documentVersion: { uri: 'urn:oid:0.9.2342.19200300.100.1.13', friendlyName: 'documentVersion' },
  domainComponent: { uri: 'urn:oid:0.9.2342.19200300.100.1.25', friendlyName: 'domainComponent' },
  drink: { uri: 'urn:oid:0.9.2342.19200300.100.1.5', friendlyName: 'drink' },
  eduOrgHomePageURI: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.2.1.2', friendlyName: 'eduOrgHomePageURI' },
  eduOrgIdentityAuthNPolicyURI: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.2.1.3', friendlyName: 'eduOrgIdentityAuthNPolicyURI' },
  eduOrgLegalName: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.2.1.4', friendlyName: 'eduOrgLegalName' },
  eduOrgSuperiorURI: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.2.1.5', friendlyName: 'eduOrgSuperiorURI' },
  eduOrgWhitePagesURI: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.2.1.6', friendlyName: 'eduOrgWhitePagesURI' },
  eduPersonAffiliation: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.1', friendlyName: 'eduPersonAffiliation' },
  eduPersonEntitlement: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.7', friendlyName: 'eduPersonEntitlement' },
  eduPersonNickname: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.2', friendlyName: 'eduPersonNickname' },
  eduPersonOrgDN: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.3', friendlyName: 'eduPersonOrgDN' },
  eduPersonOrgUnitDN: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.4', friendlyName: 'eduPersonOrgUnitDN' },
  eduPersonPrimaryAffiliation: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.5', friendlyName: 'eduPersonPrimaryAffiliation' },
  eduPersonPrimaryOrgUnitDN: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.8', friendlyName: 'eduPersonPrimaryOrgUnitDN' },
  eduPersonPrincipalName: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.6', friendlyName: 'eduPersonPrincipalName' },
  eduPersonScopedAffiliation: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.9', friendlyName: 'eduPersonScopedAffiliation' },
  eduPersonTargetedID: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.1.1.10', friendlyName: 'eduPersonTargetedID' },
  email: { uri: 'urn:oid:1.2.840.113549.1.9.1', friendlyName: 'email' },
  employeeNumber: { uri: 'urn:oid:2.16.840.1.113730.3.1.3', friendlyName: 'employeeNumber' },
  employeeType: { uri: 'urn:oid:2.16.840.1.113730.3.1.4', friendlyName: 'employeeType' },
  enhancedSearchGuide: { uri: 'urn:oid:2.5.4.47', friendlyName: 'enhancedSearchGuide' },
  facsimileTelephoneNumber: { uri: 'urn:oid:2.5.4.23', friendlyName: 'facsimileTelephoneNumber' },
  favouriteDrink: { uri: 'urn:oid:0.9.2342.19200300.100.1.5', friendlyName: 'favouriteDrink' },
  fax: { uri: 'urn:oid:2.5.4.23', friendlyName: 'fax' },
  federationFeideSchemaVersion: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.9', friendlyName: 'federationFeideSchemaVersion' },
  friendlyCountryName: { uri: 'urn:oid:0.9.2342.19200300.100.1.43', friendlyName: 'friendlyCountryName' },
  generationQualifier: { uri: 'urn:oid:2.5.4.44', friendlyName: 'generationQualifier' },
  givenName: { uri: 'urn:oid:2.5.4.42', friendlyName: 'givenName' },
  homePhone: { uri: 'urn:oid:0.9.2342.19200300.100.1.20', friendlyName: 'homePhone' },
  homePostalAddress: { uri: 'urn:oid:0.9.2342.19200300.100.1.39', friendlyName: 'homePostalAddress' },
  homeTelephoneNumber: { uri: 'urn:oid:0.9.2342.19200300.100.1.20', friendlyName: 'homeTelephoneNumber' },
  host: { uri: 'urn:oid:0.9.2342.19200300.100.1.9', friendlyName: 'host' },
  houseIdentifier: { uri: 'urn:oid:2.5.4.51', friendlyName: 'houseIdentifier' },
  info: { uri: 'urn:oid:0.9.2342.19200300.100.1.4', friendlyName: 'info' },
  initials: { uri: 'urn:oid:2.5.4.43', friendlyName: 'initials' },
  internationaliSDNNumber: { uri: 'urn:oid:2.5.4.25', friendlyName: 'internationaliSDNNumber' },
  isMemberOf: { uri: 'urn:oid:1.3.6.1.4.1.5923.1.5.1.1', friendlyName: 'isMemberOf' },
  janetMailbox: { uri: 'urn:oid:0.9.2342.19200300.100.1.46', friendlyName: 'janetMailbox' },
  jpegPhoto: { uri: 'urn:oid:0.9.2342.19200300.100.1.60', friendlyName: 'jpegPhoto' },
  knowledgeInformation: { uri: 'urn:oid:2.5.4.2', friendlyName: 'knowledgeInformation' },
  l: { uri: 'urn:oid:2.5.4.7', friendlyName: 'l' },
  labeledURI: { uri: 'urn:oid:1.3.6.1.4.1.250.1.57', friendlyName: 'labeledURI' },
  localityName: { uri: 'urn:oid:2.5.4.7', friendlyName: 'localityName' },
  mDRecord: { uri: 'urn:oid:0.9.2342.19200300.100.1.27', friendlyName: 'mDRecord' },
  mXRecord: { uri: 'urn:oid:0.9.2342.19200300.100.1.28', friendlyName: 'mXRecord' },
  mail: { uri: 'urn:oid:0.9.2342.19200300.100.1.3', friendlyName: 'mail' },
  mailPreferenceOption: { uri: 'urn:oid:0.9.2342.19200300.100.1.47', friendlyName: 'mailPreferenceOption' },
  manager: { uri: 'urn:oid:0.9.2342.19200300.100.1.10', friendlyName: 'manager' },
  member: { uri: 'urn:oid:2.5.4.31', friendlyName: 'member' },
  mobile: { uri: 'urn:oid:0.9.2342.19200300.100.1.41', friendlyName: 'mobile' },
  mobileTelephoneNumber: { uri: 'urn:oid:0.9.2342.19200300.100.1.41', friendlyName: 'mobileTelephoneNumber' },
  nSRecord: { uri: 'urn:oid:0.9.2342.19200300.100.1.29', friendlyName: 'nSRecord' },
  name: { uri: 'urn:oid:2.5.4.41', friendlyName: 'name' },
  norEduOrgAcronym: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.6', friendlyName: 'norEduOrgAcronym' },
  norEduOrgNIN: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.12', friendlyName: 'norEduOrgNIN' },
  norEduOrgSchemaVersion: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.11', friendlyName: 'norEduOrgSchemaVersion' },
  norEduOrgUniqueIdentifier: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.7', friendlyName: 'norEduOrgUniqueIdentifier' },
  norEduOrgUniqueNumber: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.1', friendlyName: 'norEduOrgUniqueNumber' },
  norEduOrgUnitUniqueIdentifier: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.8', friendlyName: 'norEduOrgUnitUniqueIdentifier' },
  norEduOrgUnitUniqueNumber: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.2', friendlyName: 'norEduOrgUnitUniqueNumber' },
  norEduPersonBirthDate: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.3', friendlyName: 'norEduPersonBirthDate' },
  norEduPersonLIN: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.4', friendlyName: 'norEduPersonLIN' },
  norEduPersonNIN: { uri: 'urn:oid:1.3.6.1.4.1.2428.90.1.5', friendlyName: 'norEduPersonNIN' },
  objectClass: { uri: 'urn:oid:2.5.4.0', friendlyName: 'objectClass' },
  organizationName: { uri: 'urn:oid:2.5.4.10', friendlyName: 'organizationName' },
  organizationalStatus: { uri: 'urn:oid:0.9.2342.19200300.100.1.45', friendlyName: 'organizationalStatus' },
  organizationalUnitName: { uri: 'urn:oid:2.5.4.11', friendlyName: 'organizationalUnitName' },
  otherMailbox: { uri: 'urn:oid:0.9.2342.19200300.100.1.22', friendlyName: 'otherMailbox' },
  owner: { uri: 'urn:oid:2.5.4.32', friendlyName: 'owner' },
  pager: { uri: 'urn:oid:0.9.2342.19200300.100.1.42', friendlyName: 'pager' },
  pagerTelephoneNumber: { uri: 'urn:oid:0.9.2342.19200300.100.1.42', friendlyName: 'pagerTelephoneNumber' },
  personalSignature: { uri: 'urn:oid:0.9.2342.19200300.100.1.53', friendlyName: 'personalSignature' },
  personalTitle: { uri: 'urn:oid:0.9.2342.19200300.100.1.40', friendlyName: 'personalTitle' },
  photo: { uri: 'urn:oid:0.9.2342.19200300.100.1.7', friendlyName: 'photo' },
  physicalDeliveryOfficeName: { uri: 'urn:oid:2.5.4.19', friendlyName: 'physicalDeliveryOfficeName' },
  pkcs9email: { uri: 'urn:oid:1.2.840.113549.1.9.1', friendlyName: 'pkcs9email' },
  postOfficeBox: { uri: 'urn:oid:2.5.4.18', friendlyName: 'postOfficeBox' },
  postalAddress: { uri: 'urn:oid:2.5.4.16', friendlyName: 'postalAddress' },
  postalCode: { uri: 'urn:oid:2.5.4.17', friendlyName: 'postalCode' },
  preferredDeliveryMethod: { uri: 'urn:oid:2.5.4.28', friendlyName: 'preferredDeliveryMethod' },
  preferredLanguage: { uri: 'urn:oid:2.16.840.1.113730.3.1.39', friendlyName: 'preferredLanguage' },
  presentationAddress: { uri: 'urn:oid:2.5.4.29', friendlyName: 'presentationAddress' },
  protocolInformation: { uri: 'urn:oid:2.5.4.48', friendlyName: 'protocolInformation' },
  pseudonym: { uri: 'urn:oid:2.5.4.65', friendlyName: 'pseudonym' },
  registeredAddress: { uri: 'urn:oid:2.5.4.26', friendlyName: 'registeredAddress' },
  rfc822Mailbox: { uri: 'urn:oid:0.9.2342.19200300.100.1.3', friendlyName: 'rfc822Mailbox' },
  roleOccupant: { uri: 'urn:oid:2.5.4.33', friendlyName: 'roleOccupant' },
  roomNumber: { uri: 'urn:oid:0.9.2342.19200300.100.1.6', friendlyName: 'roomNumber' },
  sOARecord: { uri: 'urn:oid:0.9.2342.19200300.100.1.30', friendlyName: 'sOARecord' },
  searchGuide: { uri: 'urn:oid:2.5.4.14', friendlyName: 'searchGuide' },
  secretary: { uri: 'urn:oid:0.9.2342.19200300.100.1.21', friendlyName: 'secretary' },
  seeAlso: { uri: 'urn:oid:2.5.4.34', friendlyName: 'seeAlso' },
  serialNumber: { uri: 'urn:oid:2.5.4.5', friendlyName: 'serialNumber' },
  singleLevelQuality: { uri: 'urn:oid:0.9.2342.19200300.100.1.50', friendlyName: 'singleLevelQuality' },
  stateOrProvinceName: { uri: 'urn:oid:2.5.4.8', friendlyName: 'stateOrProvinceName' },
  street: { uri: 'urn:oid:2.5.4.9', friendlyName: 'street' },
  streetAddress: { uri: 'urn:oid:2.5.4.9', friendlyName: 'streetAddress' },
  subtreeMaximumQuality: { uri: 'urn:oid:0.9.2342.19200300.100.1.52', friendlyName: 'subtreeMaximumQuality' },
  subtreeMinimumQuality: { uri: 'urn:oid:0.9.2342.19200300.100.1.51', friendlyName: 'subtreeMinimumQuality' },
  supportedAlgorithms: { uri: 'urn:oid:2.5.4.52', friendlyName: 'supportedAlgorithms' },
  supportedApplicationContext: { uri: 'urn:oid:2.5.4.30', friendlyName: 'supportedApplicationContext' },
  surname: { uri: 'urn:oid:2.5.4.4', friendlyName: 'surname' },
  telephoneNumber: { uri: 'urn:oid:2.5.4.20', friendlyName: 'telephoneNumber' },
  teletexTerminalIdentifier: { uri: 'urn:oid:2.5.4.22', friendlyName: 'teletexTerminalIdentifier' },
  telexNumber: { uri: 'urn:oid:2.5.4.21', friendlyName: 'telexNumber' },
  textEncodedORAddress: { uri: 'urn:oid:0.9.2342.19200300.100.1.2', friendlyName: 'textEncodedORAddress' },
  title: { uri: 'urn:oid:2.5.4.12', friendlyName: 'title' },
  uniqueIdentifier: { uri: 'urn:oid:0.9.2342.19200300.100.1.44', friendlyName: 'uniqueIdentifier' },
  uniqueMember: { uri: 'urn:oid:2.5.4.50', friendlyName: 'uniqueMember' },
  userCertificate: { uri: 'urn:oid:2.5.4.36', friendlyName: 'userCertificate' },
  userClass: { uri: 'urn:oid:0.9.2342.19200300.100.1.8', friendlyName: 'userClass' },
  userPKCS12: { uri: 'urn:oid:2.16.840.1.113730.3.1.216', friendlyName: 'userPKCS12' },
  userPassword: { uri: 'urn:oid:2.5.4.35', friendlyName: 'userPassword' },
  userSMIMECertificate: { uri: 'urn:oid:2.16.840.1.113730.3.1.40', friendlyName: 'userSMIMECertificate' },
  userid: { uri: 'urn:oid:0.9.2342.19200300.100.1.1', friendlyName: 'userid' },
  x121Address: { uri: 'urn:oid:2.5.4.24', friendlyName: 'x121Address' },
  x500UniqueIdentifier: { uri: 'urn:oid:2.5.4.45', friendlyName: 'x500UniqueIdentifier' },
};

export const samlNecesaryAttributes = {
  surname: {
    uri: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
    shibbolethUri: 'urn:oid:2.5.4.4',
    friendlyName: 'surname',
    oidcStandardClaim: 'family_name',
  },
  givenName: {
    uri: '​http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
    shibbolethUri: 'urn:oid:2.5.4.42',
    friendlyName: 'givenName',
    oidcStandardClaim: 'given_name',
  },
  email: {
    uri: '​http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    shibbolethUri: 'urn:oid:0.9.2342.19200300.100.1.3',
    friendlyName: 'email',
    oidcStandardClaim: 'email',
  },
  groups: {
    uri: 'http://schemas.microsoft.com/ws/2008/06/identity/claims/groups',
    shibbolethUri: '',
    friendlyName: 'groups',
    oidcStandardClaim: 'groups',
  },
}

export default {
  parseSamlIDPXml,
  samlAttributeNames,
  samlNecesaryAttributes,
};
